import * as yup from "yup"

/**
 * CF7フォームの共通フロー制御（contact / form などで共有）
 * 入力 → 確認(readonly) → 完了(リダイレクト)。yupでバリデーション、条件表示にも対応。
 * @typedef {"input" | "confirm"} FormPhase
 */

// yupロケール設定
yup.setLocale({
  mixed: {
    required: "入力して下さい。",
    notType: "正しい形式で入力して下さい。",
  },
  string: {
    email: "正しい形式で入力して下さい。",
    url: "正しい形式で入力して下さい。",
  },
})

const telRule = {
  message: "半角数字とハイフンで入力して下さい。",
  excludeEmptyString: true,
}

// --- お問い合わせ（contact）スキーマ ---
const contactSchema = yup.object().shape({
  "inquiry-type": yup.string().required("選択して下さい。"),
  company: yup.string().required(),
  "your-name": yup.string().required(),
  "your-email": yup.string().required().email(),
  tel: yup.string().required().matches(/^[0-9-]*$/, telRule),
  url: yup.string().url(), // CF7の[url]と整合（任意・入力時のみURL形式チェック。空はexcludeEmptyStringで許容）
  budget: yup.string(),
  source: yup.string().required("選択して下さい。"),
  "source-other": yup.string().when("source", {
    is: "その他",
    then: (s) => s.required("詳細を入力して下さい。"),
  }),
  message: yup.string().required(),
  privacy: yup.boolean().oneOf([true], "個人情報の取り扱いに同意して下さい。"),
})

// --- 取引開始前登録（register）スキーマ ---
const registerSchema = yup.object().shape({
  "entity-name": yup.string().required(),
  "invoice-status": yup.string().required("選択して下さい。"),
  "invoice-number": yup.string().when("invoice-status", {
    is: "登録している",
    then: (s) => s.required("登録番号を入力して下さい。"),
  }),
  "invoice-date-y": yup.string().when("invoice-status", {
    is: "登録する予定",
    then: (s) => s.required("登録申請予定日を選択して下さい。"),
  }),
  "invoice-date-m": yup.string().when("invoice-status", {
    is: "登録する予定",
    then: (s) => s.required("登録申請予定日を選択して下さい。"),
  }),
  "invoice-date-d": yup.string().when("invoice-status", {
    is: "登録する予定",
    then: (s) => s.required("登録申請予定日を選択して下さい。"),
  }),
  "contact-name": yup.string().required(),
  "influencer-name": yup.string().required(),
  "sns-url": yup.string().required(),
  address: yup.string().required(),
  "sample-address": yup.string(),
  tel: yup.string().required().matches(/^[0-9-]*$/, telRule),
  email: yup.string().required().email(),
  "bank-name": yup.string().required(),
  "branch-name": yup.string().required(),
  "account-type": yup.string().required("選択して下さい。"),
  "account-number": yup.string().required(),
  "account-holder": yup.string().required(),
  "agree-pledge": yup.boolean().oneOf([true], "取引開始の誓約に同意して下さい。"),
  "agree-privacy": yup.boolean().oneOf([true], "個人情報の取り扱いに同意して下さい。"),
})

/** data-form-flow の値 → スキーマ */
const schemas: Record<string, yup.ObjectSchema<any>> = {
  contact: contactSchema,
  register: registerSchema,
}

/**
 * フォームの現在値を収集する（バリデーション用）
 * @param {HTMLFormElement} form - CF7フォーム要素
 * @returns {Record<string, string | boolean>} フィールド名→値
 */
const collectValues = (form: HTMLFormElement): Record<string, string | boolean> => {
  const data = new FormData(form)
  const values: Record<string, string | boolean> = {}

  for (const [key, value] of data.entries()) {
    if (typeof value === "string") values[key] = value
  }

  // チェックボックスは真偽値で上書き（未チェックはFormDataに含まれないため）
  form.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((cb) => {
    values[cb.name] = cb.checked
  })

  return values
}

/**
 * フィールド名から、エラー表示・状態付与の対象ラッパーを取得する
 * @param {HTMLFormElement} form - CF7フォーム要素
 * @param {string} name - CF7フィールド名
 * @returns {HTMLElement | null}
 */
const getFieldWrap = (form: HTMLFormElement, name: string): HTMLElement | null => {
  const wrap = form.querySelector(`.wpcf7-form-control-wrap[data-name="${name}"]`)
  return wrap?.closest<HTMLElement>(".Form__field, .Form__agree") ?? null
}

/**
 * 全フィールドのエラー表示をクリアする
 * @param {HTMLFormElement} form - CF7フォーム要素
 */
const clearErrors = (form: HTMLFormElement): void => {
  form.querySelectorAll(".Form__field, .Form__agree").forEach((el) => {
    el.classList.remove("hasError")
    el.querySelector(".Form__error")?.remove()
  })
}

/**
 * バリデーションエラーを各フィールドに表示する
 * @param {HTMLFormElement} form - CF7フォーム要素
 * @param {yup.ValidationError} err - yupのバリデーションエラー
 * @returns {HTMLElement | null} 最初のエラー要素（スクロール先）
 */
const showErrors = (form: HTMLFormElement, err: yup.ValidationError): HTMLElement | null => {
  let firstErrorWrap: HTMLElement | null = null
  if (!err.inner) return null

  err.inner.forEach((e) => {
    if (!e.path) return
    const wrap = getFieldWrap(form, e.path)
    if (!wrap) return

    if (!firstErrorWrap) firstErrorWrap = wrap
    wrap.classList.add("hasError")

    // 1ラッパーにつきエラーは1つだけ表示（グループ内の複数エラーは集約）
    if (!wrap.querySelector(".Form__error")) {
      const span = document.createElement("span")
      span.className = "Form__error"
      span.textContent = e.message
      wrap.appendChild(span)
    }
  })

  return firstErrorWrap
}

/**
 * CF7サーバー側のバリデーションエラーを、yupと同じスタイル（.Form__error）で各フィールドに表示する。
 * CF7の apiResponse.invalid_fields（各要素の into=対象ラッパーのセレクタ, message=メッセージ）を流用する。
 * @param {HTMLFormElement} form - CF7フォーム要素
 * @param {Array<{ into?: string; message?: string }>} fields - CF7 の invalid_fields
 * @returns {HTMLElement | null} 最初のエラー要素（スクロール先）
 */
const showCf7Errors = (
  form: HTMLFormElement,
  fields: Array<{ into?: string; message?: string }>
): HTMLElement | null => {
  let firstErrorWrap: HTMLElement | null = null

  fields.forEach((f) => {
    if (!f.into) return
    // into は .wpcf7-form-control-wrap を指すセレクタ。そこから yup と同じラッパーへ辿る
    const control = form.querySelector(f.into)
    const wrap = control?.closest<HTMLElement>(".Form__field, .Form__agree")
    if (!wrap) return

    if (!firstErrorWrap) firstErrorWrap = wrap
    wrap.classList.add("hasError")

    if (!wrap.querySelector(".Form__error")) {
      const span = document.createElement("span")
      span.className = "Form__error"
      span.textContent = f.message ?? ""
      wrap.appendChild(span)
    }
  })

  return firstErrorWrap
}

/**
 * フォームの入力可否を切り替える（確認フェーズでは readonly 化）
 * @param {HTMLFormElement} form - CF7フォーム要素
 * @param {boolean} locked - true で編集不可
 */
const setFormLocked = (form: HTMLFormElement, locked: boolean): void => {
  form
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      "input[type='text'], input[type='email'], input[type='tel'], input[type='url'], textarea"
    )
    .forEach((el) => {
      if (locked) el.setAttribute("readonly", "")
      else el.removeAttribute("readonly")
    })

  // select / radio / checkbox は readonly 不可のため、キーボード操作を無効化（値は保持）
  form
    .querySelectorAll<HTMLElement>("select, input[type='radio'], input[type='checkbox']")
    .forEach((el) => {
      ;(el as HTMLInputElement).tabIndex = locked ? -1 : 0
    })
}

/**
 * ステッパーの表示状態を更新する（現在ステップのみ強調）
 * @param {NodeListOf<HTMLElement>} steps - .Form__stepperItem のリスト
 * @param {string} phase - 現在のフェーズ
 */
const updateStepper = (steps: NodeListOf<HTMLElement>, phase: string): void => {
  steps.forEach((step) => {
    step.classList.toggle("is-current", step.dataset.step === phase)
  })
}

/**
 * 条件表示フィールドを初期化する
 * [data-cond-controller] 内の select 値に応じて、同グループ内の
 * .Form__cond[data-show-when] を出し分ける。
 * @param {HTMLFormElement} form - CF7フォーム要素
 */
const initConditionalFields = (form: HTMLFormElement): void => {
  form.querySelectorAll<HTMLSelectElement>("[data-cond-controller] select").forEach((sel) => {
    const group = sel.closest<HTMLElement>(".Form__field--group")
    if (!group) return

    const conds = group.querySelectorAll<HTMLElement>(".Form__cond[data-show-when]")
    const update = () => {
      conds.forEach((c) => {
        c.hidden = c.dataset.showWhen !== sel.value
      })
    }

    sel.addEventListener("change", update)
    update()
  })
}

/**
 * 1つのフォームフローを初期化する
 * @param {HTMLElement} root - [data-form-flow] を持つルート要素
 */
const initFormFlow = (root: HTMLElement): void => {
  const form = root.querySelector<HTMLFormElement>("form.wpcf7-form")
  const nextBtn = root.querySelector<HTMLButtonElement>('.Form__btn[data-action="next"]')
  const backBtn = root.querySelector<HTMLButtonElement>('.Form__btn[data-action="back"]')
  const submitBtn = root.querySelector<HTMLButtonElement>('.Form__btn[data-action="submit"]')
  const realSubmit = form?.querySelector<HTMLInputElement>('input[type="submit"]')
  const steps = root.querySelectorAll<HTMLElement>(".Form__stepperItem")
  const thanksUrl = root.dataset.thanks ?? "/"
  const schema = schemas[root.dataset.formFlow ?? ""] ?? yup.object()

  // フォームが無いページ（完了ページ等）は何もしない
  if (!form || !nextBtn || !backBtn || !submitBtn || !realSubmit) return

  // このフォームのCF7 ID（wpcf7イベントを自フォーム宛だけに絞る用）
  const cf7Id = form.querySelector<HTMLInputElement>('input[name="_wpcf7"]')?.value ?? ""

  initConditionalFields(form)

  // 誓約・個人情報同意の枠内スクロール(.Form__termsScroll)はPCでLenisにスクロールを奪われて
  // 中身をスクロールできないので data-lenis-prevent で除外（ヘッダードロワーと同じ手法）。
  root.querySelectorAll<HTMLElement>(".Form__termsScroll").forEach((el) => el.setAttribute("data-lenis-prevent", ""))

  let phase = "input"

  // Enterキーによる意図しない送信を防止（textarea以外）
  form.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && (e.target as HTMLElement).tagName !== "TEXTAREA") {
      e.preventDefault()
    }
  })

  /**
   * フェーズを切り替える
   * @param {string} next - 切り替え先フェーズ
   */
  const setPhase = (next: string): void => {
    phase = next
    root.classList.toggle("is-confirm", phase === "confirm")
    setFormLocked(form, phase === "confirm")
    updateStepper(steps, phase)
  }

  // 「送信する / 確認する」（入力 → 確認）
  nextBtn.addEventListener("click", async () => {
    clearErrors(form)
    const values = collectValues(form)

    try {
      await schema.validate(values, { abortEarly: false })
      setPhase("confirm")
      root.querySelector(".Form")?.scrollIntoView({ behavior: "smooth", block: "start" })
    } catch (err) {
      const first = showErrors(form, err as yup.ValidationError)
      first?.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  })

  // 「入力に戻る」
  backBtn.addEventListener("click", () => {
    setPhase("input")
    root.querySelector(".Form")?.scrollIntoView({ behavior: "smooth", block: "start" })
  })

  // 「この内容で登録する / 送信する」→ CF7の本来の送信をトリガー
  submitBtn.addEventListener("click", () => {
    realSubmit.click()
  })

  // 送信成功 → 完了ページへ
  document.addEventListener(
    "wpcf7mailsent",
    () => {
      window.location.href = thanksUrl
    },
    false
  )

  // サーバー側バリデーションNG → 入力に戻し、yupと同じスタイルでフィールドごとにエラー表示
  document.addEventListener(
    "wpcf7invalid",
    (e) => {
      const detail = (e as CustomEvent).detail
      // 別フォームのイベントは無視（自フォーム宛のみ）
      if (cf7Id && String(detail?.contactFormId ?? "") !== cf7Id) return

      setPhase("input")
      clearErrors(form)
      const first = showCf7Errors(form, detail?.apiResponse?.invalid_fields ?? [])
      // 個別表示するのでCF7の総合バナー(.wpcf7-response-output)はサマリ重複になるため隠す（:emptyで非表示）
      const banner = form.querySelector<HTMLElement>(".wpcf7-response-output")
      if (banner) banner.textContent = ""
      first?.scrollIntoView({ behavior: "smooth", block: "center" })
    },
    false
  )

  // スパム判定 / メール送信失敗 → 入力に戻す（フィールド単位のエラーは無いので総合バナー .wpcf7-response-output で通知）
  ;["wpcf7spam", "wpcf7mailfailed"].forEach((evt) => {
    document.addEventListener(evt, () => setPhase("input"), false)
  })

  // 初期化
  setPhase("input")
}

// ページ内の全フォームフローを初期化
document.querySelectorAll<HTMLElement>("[data-form-flow]").forEach(initFormFlow)
